import { createUseStyles } from 'react-jss';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { Popover, Stack, useMediaQuery } from '@mui/material';
import { useMemo, useRef } from 'react';

import { Icon } from '../Icon';
import type { FullScreenHandle } from '../FullScreen';
import { theme } from '../../styles/theme';
import { useHeatMapFilterOptions } from '../../map-data/use-heat-map-filter-options';
import { usePoiFilterOptions } from '../../map-data/use-filter-options';
import { usePoiSelectedLocations } from '../PointOfInterestExperience/PoiSelectedLocationsProvider';
import { useHeatMapSelectedLocations } from '../HeatMapExperience/HeatMapSelectedLocationsProvider';
import { Typography } from '../Typography';
import { PointOfInterestFilters } from '../PointOfInterestExperience/PointOfInterestFilters';
import { HeatMapFilters } from '../HeatMapExperience/HeatMapFilters';
import { useFavouritesData } from '../../providers/FavouritesProvider';
import { useFilterMapData } from '../../map-data/use-filter-poi-map-data';
import { filterAppliesToViewMode } from '../../map-data/heatmap-filter-registry';

import { DropDownMenu } from './DropDownMenu';
import { MapSelection } from './MapSelection';
import type { MapExperience } from './NavigationBarProvider';
import {
  useNavigationBarData,
  useUpdateNavigationBar,
} from './NavigationBarProvider';

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    '& > *': { width: 'calc(50% - 2px)' },
    '&.bottom': {
      flexWrap: 'nowrap',
      gap: 2,
      '& > *': { width: 'auto', flex: '1 1 0' },
    },
  },
  compactButton: {
    flex: '0 0 auto !important',
    width: 'auto !important',
    gap: theme.spacing(2),
    '& > div': {
      paddingInlineEnd: theme.spacing(6),
    },
  },

  navigationButton: {
    display: 'inline-flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(4),
    height: '100%',
    backgroundColor: theme.colors['grey-100'],
    borderBottom: `1px solid ${theme.colors['grey-100']}`,
    textAlign: 'left',
    '&:hover, &:focus': {
      textDecoration: 'underline',
      backgroundColor: theme.colors['green-10'],
    },
    '& > div:last-child': {
      textAlign: 'start',
    },
  },
  navigationButtonTight: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },

  navigationButtonActive: {
    borderBottom: `1px solid ${theme.colors['green-100']}`,
    background: `linear-gradient(180deg, #16887D 0%, #008074 100%)`,
    color: theme.colors.white,
  },

  navigationButtonLight: {
    borderTop: `4px solid ${theme.colors['grey-100']}`,
    backgroundColor: theme.colors['white'],
    color: theme.colors['green-100'],
    gap: theme.spacing(2),
    '&.active': {
      backgroundColor: theme.colors['green-90'],
      color: theme.colors.white,
    },
    '&[disabled]': {
      color: 'rgba(255, 255, 255, 0.4)',
      backgroundColor: theme.colors['darker'],
      '& img': {
        opacity: 0.4,
      },
    },
  },

  dropDown: {
    display: 'none',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    zIndex: 9,
  },

  showOnTabletPlus: {
    display: 'none',
  },

  desktopOnly: {
    display: 'none',
  },

  wideScreenOnly: {
    display: 'none',
  },

  mobileNavigation: {
    display: 'none',
  },

  mobileNavigationButton: {
    flex: 1,
    backgroundColor: theme.colors['white'],
    padding: theme.spacing(2),
    color: theme.colors['green-100'],
    '&.active': {
      backgroundColor: theme.colors['green-100'],
      color: theme.colors.white,
    },
  },

  buttonTitle: {
    '.bottom &': {
      width: '100%',
    },
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
    justifyContent: 'start',
    '& .expand': {
      marginLeft: 'auto',
    },
  },
  buttonSubtitle: {
    paddingLeft: theme.spacing(4),
    marginLeft: 24,
  },

  /* mobile and tablet styling */
  [`@media screen and (max-width: ${theme.breakPoints.desktop}px)`]: {
    container: {
      '&&': {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.colors['grey-100'],
        zIndex: 9,
        position: 'fixed',
        maxHeight: '100vh',
        overflowY: 'scroll',
        bottom: 0,
        left: 0,
        right: 0,
      },
    },

    navigationButton: {
      '&&': {
        display: 'none',
      },
    },
    mobileNavigation: {
      '&&': {
        backgroundColor: theme.colors['grey-100'],
        padding: theme.spacing(2),
        display: 'flex',
        gap: theme.spacing(1),
      },
    },
    dropDown: {
      '&&': {
        display: 'block',
      },
    },
  },
  [`@media screen and (min-width: ${theme.breakPoints.tablet}px)`]: {
    showOnTabletPlus: {
      '&&': {
        display: 'block',
      },
    },
  },
  [`@media screen and (min-width: ${theme.breakPoints.desktop}px)`]: {
    desktopOnly: {
      '&&': {
        display: 'block',
      },
    },
  },
  [`@media screen and (min-width: ${theme.breakPoints.widescreen}px)`]: {
    wideScreenOnly: {
      '&&': {
        display: 'block',
      },
    },
  },
});

interface NavigationBarProps {
  onFavouritesClick?: () => void;
  fullScreenHandle?: FullScreenHandle;
}

export function NavigationBar({
  onFavouritesClick,
  fullScreenHandle,
}: NavigationBarProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const isDesktop = useMediaQuery(
    `(min-width: ${theme.breakPoints.desktop}px)`,
  );

  const { selectedNavigationItem, selectedExperience, setSelectedExperience } =
    useNavigationBarData();
  const { updateSelectedNavigationItem } = useUpdateNavigationBar();
  const { poiFavourites, heatmapFavourites } = useFavouritesData();

  const { resetExperience: resetPoiMap } = usePoiSelectedLocations();
  const { resetExperience: resetHeatMap } = useHeatMapSelectedLocations();
  const {
    resetFilterOptions: resetHeatMapFilters,
    filterOptions: heatmapFilterOptions,
    filteredCountryCount,
    rangeType,
  } = useHeatMapFilterOptions();
  const {
    resetFilterOptions: resetPoiMapFilters,
    filterOptions: poiFilterOptions,
  } = usePoiFilterOptions();
  const { filteredRecordCount } = useFilterMapData();

  const isPinMap = selectedExperience !== 'heatMap';

  const handleNavigationToMap = (view: MapExperience) => {
    setSelectedExperience(view);
    updateSelectedNavigationItem('map');

    if (view === 'heatMap') {
      resetPoiMap();
      resetPoiMapFilters();
    }

    if (view === 'pinMap') {
      resetHeatMap();
      resetHeatMapFilters();
    }
  };

  const filterCount = useMemo(() => {
    const filters: { [key: string]: any } =
      selectedExperience === 'pinMap' ? poiFilterOptions : heatmapFilterOptions;

    return Object.keys(filters || {}).filter(k => {
      const value = filters[k];
      // Exclude undefined, 'AllStatuses', and empty arrays
      if (value === undefined || value === 'AllStatuses') return false;
      if (Array.isArray(value) && value.length === 0) return false;

      // For heatmap, check if the filter applies to the current view mode
      if (
        selectedExperience === 'heatMap' &&
        !filterAppliesToViewMode(k, rangeType)
      ) {
        return false; // Filter doesn't apply to current view mode
      }

      return true;
    }).length;
  }, [selectedExperience, poiFilterOptions, heatmapFilterOptions, rangeType]);

  const favouritesCount = useMemo(() => {
    if (selectedExperience === 'pinMap') {
      return poiFavourites.length;
    }
    if (selectedExperience === 'heatMap') {
      return heatmapFavourites.length;
    }
    return poiFavourites.length + heatmapFavourites.length;
  }, [selectedExperience, poiFavourites.length, heatmapFavourites.length]);

  const recordCount = useMemo(() => {
    if (selectedExperience === 'pinMap') {
      return filteredRecordCount;
    }
    return filteredCountryCount;
  }, [selectedExperience, filteredRecordCount, filteredCountryCount]);

  return (
    <>
      <div className={classes.dropDown}>
        {selectedNavigationItem === 'map' && (
          <DropDownMenu title={'Map'}>
            <MapSelection />
          </DropDownMenu>
        )}
        {selectedNavigationItem === 'filter' && (
          <DropDownMenu title="Filter">
            {isPinMap ? <PointOfInterestFilters /> : <HeatMapFilters />}
          </DropDownMenu>
        )}

        <div className={classes.mobileNavigation}>
          <button
            onClick={() =>
              updateSelectedNavigationItem(
                selectedNavigationItem === 'map' ? undefined : 'map',
              )
            }
            className={clsx(classes.mobileNavigationButton, {
              active: selectedNavigationItem === 'map',
            })}
          >
            <Stack spacing={0.5}>
              <div className={'icon'}>
                <Icon
                  variant={
                    selectedNavigationItem === 'map' ? 'ActiveMap' : 'Map'
                  }
                  width={24}
                  height={24}
                />
              </div>
              <Typography variant={'p4'} className="title">
                Map
              </Typography>
            </Stack>
          </button>
          <button
            onClick={() =>
              updateSelectedNavigationItem(
                selectedNavigationItem === 'filter' ? undefined : 'filter',
              )
            }
            className={clsx(classes.mobileNavigationButton, {
              active: selectedNavigationItem === 'filter',
            })}
          >
            <Stack spacing={0.5}>
              <div className={'icon'}>
                <Icon
                  variant={
                    selectedNavigationItem === 'filter'
                      ? 'ActiveFilter'
                      : 'Filter'
                  }
                  width={24}
                  height={24}
                />
              </div>
              <Typography variant={'p4'} className="title">
                {selectedExperience === 'pinMap'
                  ? t('navigationBar.poiFiltersTitle')
                  : t('navigationBar.heatMapFiltersTitle')}
              </Typography>
            </Stack>
          </button>
          <button
            onClick={onFavouritesClick}
            className={clsx(classes.mobileNavigationButton)}
            disabled={favouritesCount === 0}
          >
            <Stack spacing={0.5}>
              <div className={'icon'} style={{ position: 'relative' }}>
                <Icon
                  variant={favouritesCount > 0 ? 'HeartFilled' : 'Heart'}
                  width={20}
                  height={20}
                />
                {favouritesCount > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      background: '#e63946',
                      color: 'white',
                      borderRadius: '50%',
                      width: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {favouritesCount}
                  </div>
                )}
              </div>
              <Typography variant={'p4'} className="title">
                {t('navigationBar.favourites', { defaultValue: 'Favourites' })}
              </Typography>
            </Stack>
          </button>
        </div>
      </div>
      <div className={classes.container} id="map-navigation-buttons">
        <button
          className={clsx(classes.navigationButton, {
            [classes.navigationButtonActive]: selectedExperience !== 'heatMap',
          })}
          onClick={() => handleNavigationToMap('pinMap')}
        >
          <div className={classes.buttonTitle}>
            {selectedExperience === 'pinMap' ? (
              <Icon variant="ActiveMap" width={20} height={20} />
            ) : (
              <Icon variant="Map" width={20} height={20} />
            )}
            <Typography variant="h3" fontWeight={400}>
              {t('navigationBar.poiTitle', { defaultValue: 'Pin map' })}
            </Typography>
          </div>
          <div className={classes.buttonSubtitle}>
            <Typography variant="p3">
              {t('navigationBar.poiDescription', {
                defaultValue: 'Implementation studies and programmes',
              })}
            </Typography>
          </div>
        </button>
        <button
          className={clsx(classes.navigationButton, {
            [classes.navigationButtonActive]: selectedExperience === 'heatMap',
          })}
          onClick={() => handleNavigationToMap('heatMap')}
          id="heat-map-navigation-button"
        >
          <div className={classes.buttonTitle}>
            {selectedExperience === 'heatMap' ? (
              <Icon variant="ActiveWorld" width={20} height={20} />
            ) : (
              <Icon variant="World" width={20} height={20} />
            )}
            <Typography variant="h3">
              {t('navigationBar.heatMapTitle', {
                defaultValue: 'Heat map',
              })}
            </Typography>
          </div>
          <div className={classes.buttonSubtitle}>
            <Typography variant="p3">
              {t('navigationBar.heatMapDescription', {
                defaultValue: 'The policy context for implementation',
              })}
            </Typography>
          </div>
        </button>
      </div>
      <div className={clsx(classes.container, 'bottom')}>
        <button
          className={clsx(
            classes.navigationButton,
            classes.navigationButtonLight,
            classes.navigationButtonTight,
            {
              active: selectedNavigationItem === 'filter',
            },
          )}
          ref={anchorEl}
          onClick={() => {
            updateSelectedNavigationItem('filter');
          }}
          data-tour="filter-button"
        >
          <div className={classes.buttonTitle}>
            <Icon
              variant="ActiveFilter"
              width={18}
              height={18}
              color="inherit"
            />
            <Typography variant="p1" fontWeight={600}>
              {t('navigationBar.mapFiltersTitle', {
                defaultValue: 'Map filters',
              })}
              &nbsp;
              {`${t('navigationBar.filtersAdded', {
                defaultValue: '({{ count }})',
                count: filterCount,
              })}`}
            </Typography>
            <Typography
              variant="p3"
              fontWeight={600}
              className={classes.showOnTabletPlus}
            >
              {t('navigationBar.recordsShowing', {
                defaultValue: 'Showing {{ count }} records',
                count: recordCount,
              })}
            </Typography>
            <Icon
              className={'expand'}
              variant={
                selectedNavigationItem === 'filter' ? 'ActiveExpand' : 'Expand'
              }
              width={18}
              height={18}
              color="inherit"
            />
          </div>
        </button>
        <button
          className={clsx(
            classes.navigationButton,
            classes.navigationButtonLight,
            classes.navigationButtonTight,
            { active: selectedNavigationItem === 'comparison' },
          )}
          onClick={() =>
            updateSelectedNavigationItem(
              selectedNavigationItem === 'comparison'
                ? undefined
                : 'comparison',
            )
          }
          data-tour="comparison-button"
        >
          <div className={classes.buttonTitle}>
            <Icon
              variant="ActiveComparison"
              width={18}
              height={18}
              color="inherit"
            />
            <Typography variant="p1" fontWeight={600}>
              {selectedExperience === 'pinMap'
                ? t('navigationBar.poiComparisonTitle', {
                    defaultValue: 'Compare implementation',
                  })
                : t('navigationBar.heatMapComparisonTitle', {
                    defaultValue: 'Compare policy',
                  })}
            </Typography>
            <Typography
              variant="p3"
              fontWeight={600}
              className={classes.wideScreenOnly}
            >
              {selectedExperience === 'pinMap'
                ? t('navigationBar.poiComparisonDescription', {
                    defaultValue:
                      'Compare records of data by selecting studies/programmes',
                  })
                : t('navigationBar.heatMapComparisonDescription', {
                    defaultValue:
                      'Compare records of data by selecting countries',
                  })}
            </Typography>
            <Icon
              className={'expand'}
              variant={
                selectedNavigationItem === 'comparison'
                  ? 'ActiveExpand'
                  : 'Expand'
              }
              width={18}
              height={18}
              color="inherit"
            />
          </div>
        </button>
        <button
          className={clsx(
            classes.navigationButton,
            classes.navigationButtonLight,
            classes.navigationButtonTight,
            classes.compactButton,
          )}
          onClick={onFavouritesClick}
          data-tour="favourites-button"
        >
          <div className={classes.buttonTitle}>
            <Icon
              variant={favouritesCount > 0 ? 'HeartFilled' : 'Heart'}
              width={18}
              height={18}
              color={theme.colors['green-100']}
            />
            <Typography variant="p1" fontWeight={600}>
              {t('navigationBar.favouritesButton', {
                defaultValue: 'Favourites ({{count}})',
                count: favouritesCount,
              })}
            </Typography>
          </div>
        </button>
        {fullScreenHandle && document.fullscreenEnabled && (
          <button
            className={clsx(
              classes.navigationButton,
              classes.navigationButtonLight,
              classes.navigationButtonTight,
              classes.compactButton,
            )}
            onClick={() =>
              fullScreenHandle.active
                ? fullScreenHandle.exit()
                : fullScreenHandle.enter()
            }
          >
            <div className={classes.buttonTitle}>
              <Icon
                variant={
                  fullScreenHandle.active
                    ? 'FullscreenContract'
                    : 'FullscreenExpand'
                }
                width={18}
                height={18}
                color={theme.colors['green-100']}
              />
              <Typography variant="p1" fontWeight={600}>
                {t('navigationBar.fullscreen', {
                  defaultValue: 'Fullscreen',
                })}
              </Typography>
            </div>
          </button>
        )}
      </div>

      {isDesktop && (
        <Popover
          id="basic-menu"
          anchorEl={anchorEl.current}
          open={selectedNavigationItem === 'filter'}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          onClose={() => updateSelectedNavigationItem(undefined)}
          PaperProps={{
            style: { maxWidth: '100vW', width: '100vW' },
          }}
          marginThreshold={0}
          transitionDuration={0}
        >
          {isPinMap ? <PointOfInterestFilters /> : <HeatMapFilters />}
        </Popover>
      )}
    </>
  );
}
